"use client";

import { memo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Icon from "@/components/ui/Icon";
import { Typography } from "@/components/ui/typography";
import Carousel, { CarouselSlide } from "@/components/ui/carousel";
import type { ServicesResourcesProps } from "./types";
import { servicesResourcesMap } from "./constants";
import { ServiceResourceCard } from "@/components/screen/global-components/services";

const ServicesResources = ({
  variant = "certified",
  className,
}: ServicesResourcesProps) => {
  const {
    cards,
    content: { title, description },
  } = servicesResourcesMap[variant];

  return (
    <section
      className={cn(
        "section overflow-hidden bg-white lg:bg-background-secondary",
        className
      )}
    >
      <div className="container mx-auto py-12 lg:py-20">
        <div className="flex flex-col gap-4 md:justify-center md:gap-2 md:w-full md:max-w-[900px] md:mx-auto md:text-center">
          <Typography variant="heading4">
            <h2>{title}</h2>
          </Typography>
          {!!description && (
            <div className="flex justify-center text-gray-600 w-full max-w-[746] mx-auto">
              <Typography variant="subHeadingSm" asChild>
                <p>{description}</p>
              </Typography>
            </div>
          )}
        </div>
        <Carousel.Mobile
          options={{
            slides: {
              spacing: 24,
              perView: "auto",
            },
          }}
          showNavigation
          className="mt-6 md:!grid md:!grid-cols-2 md:!gap-4 lg:!mt-12 lg:!grid-cols-3 !overflow-visible [&>[data-carousel-slide]:not(.carousel-slide-active)]:opacity-50"
          navigationSlot={
            <Link
              href="#"
              className="flex-auto py-3 px-4 flex items-center gap-4 min-w-0 border border-blue-500 text-[#2F80ED] text-base leading-[150%] font-semibold md:hidden rounded-3xl"
            >
              <span className="truncate flex-auto min-w-0">
                Discover more resources
              </span>
              <Icon
                id="icon-arrow-right-lg_24"
                width={24}
                height={24}
                className="shrink-0 w-6 h-6"
              />
            </Link>
          }
        >
          {cards.map(({ title, description, imageUrl }, i) => {
            return (
              <CarouselSlide
                key={i}
                className="!min-w-[82%] !w-[82%] !md:min-w-auto !w-auto transition-opacity duration-300 will-change-[opacity] md:transition-none md:!opacity-100 md:duration-initial"
              >
                <ServiceResourceCard
                  title={title}
                  imageUrl={imageUrl}
                  description={description}
                />
              </CarouselSlide>
            );
          })}
        </Carousel.Mobile>
        <div className="hidden mt-12 text-center md:flex items-center justify-center">
          <Button
            asChild
            variant="primary-outline"
            className="py-3 px-6 h-[48px] w-full sm:w-auto"
          >
            <Link href="#" className="!text-base">
              Discover more resources
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default memo(ServicesResources);
